import type { FormInstance } from 'antd';
import type { StoreState, AllActions, AttackWithId, FormConfig, POI, MapRegion, Image, ItemWithId, FormField, ImageCategory } from '../../types/types';
import EncounterCreatureFields from '../../modals/EditModal/EncounterCreatureFields';
import AttackFields from './AttackFields'; 
import ColorOpacityInputWrapper from './ColorOpacityInputWrapper';

import { useImageLibraryStore } from '../../state/createImageLibraryStore';
const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + (s ?? []).slice(1) : '');

const NPC_STATUS_OPTIONS = ['neutral', 'friendly', 'allied', 'unfriendly', 'hostile'].map(s => ({ label: capitalize(s), value: s }));
const POI_STATUS_OPTIONS = ['unexplored', 'discovered', 'cleared'].map(s => ({ label: capitalize(s), value: s }));
const ALIGNMENT_OPTIONS = ['Lawful Good', 'Lawful Neutral', 'Lawful Evil', 'Neutral Good', 'Neutral', 'Neutral Evil', 'Chaotic Good', 'Chaotic Neutral', 'Chaotic Evil'].map(a => ({ label: a, value: a }));

const getMapOptions = (item: ItemWithId | null, state: StoreState): { label: string; value: number; }[] => {
  const imageLibrary = useImageLibraryStore.getState().images;
  const imageCategories = state.session.cache.imageCategories;
  const mapCategoryIds = (imageCategories ?? []).filter(c => c.isMapCategory).map(c => c.id);
  const mapImages = (imageLibrary ?? []).filter((img: Image) => img.categoryId && mapCategoryIds.includes(img.categoryId));

  // Ensure the currently selected map is in the list, even if its category doesn't match
  if (item?.mapId && !mapImages.some((img: Image) => img.id === item.mapId)) {
    const currentMapImage = (imageLibrary ?? []).find((img: Image) => img.id === item.mapId);
    if (currentMapImage) mapImages.push(currentMapImage);
  }
  return (mapImages ?? [])
    .map((m: Image) => ({ label: m.name, value: m.id }));
};

// --- Start of Architectural Refactor ---

// We now export a function that returns the config, so it can access the `actions` from the store.
export const getFormConfig = (actions: AllActions): FormConfig => ({
  player: ({
    title: 'Player',
    width: 800,
    tabs: [
      { id: 'main', label: 'Main', fields: ([
        { name: 'name', label: 'Name', rules: [{ required: true }] },
        { name: 'race', label: 'Race' },
        { name: 'gender', label: 'Gender' },
        { name: 'className', label: 'Class' },
        { name: 'level', label: 'Level', type: 'number', initialValue: 1 },
        { name: 'mapId', label: 'Location', type: 'select', options: getMapOptions, placeholder: '-- Not on a map --',
          // When adding a new player, default their map to the currently active one.
          initialValue: (state: StoreState) => state.session.map.activeMapId,
        },
      ] as FormField[])},
      { id: 'combat', label: 'Combat', fields: [
        { name: 'maxHp', label: 'Max HP', type: 'number' },
        { name: 'ac', label: 'AC', type: 'number' },
        { name: 'dexMod', label: 'DEX Mod', type: 'number' },
        { name: 'thac0', label: 'THAC0', type: 'number' },
        { name: 'numberOfAttacks', label: '# of Attacks', type: 'number' },
      ]},
      { id: 'stats', label: 'Stats', fields: [
        { name: ['stats', 'str'], label: 'STR', type: 'number' },
        { name: ['stats', 'dex'], label: 'DEX', type: 'number' },
        { name: ['stats', 'con'], label: 'CON', type: 'number' },
        { name: ['stats', 'int'], label: 'INT', type: 'number' },
        { name: ['stats', 'wis'], label: 'WIS', type: 'number' },
        { name: ['stats', 'cha'], label: 'CHA', type: 'number' },
      ]},
      { id: 'notes', label: 'Notes', fields: [
        { name: 'notes', label: 'Notes', type: 'textarea' },
      ] as FormField[] },
      { id: 'attacks', label: 'Attacks', fields: [
        { name: 'attacks', label: '', type: 'custom', component: AttackFields },
      ]}
    ],
    addAction: actions.addPlayer,
    updateAction: actions.updatePlayer,
    buttons: { viewOnMap: true, healToFull: true, setImage: true }, // healToFull is a custom button
    createItemFromForm: (data) => {
      return {
        id: -Date.now(),
        ...data,
        // mapId defaults to activeMapId via the field's initialValue in the form config.
        // Do NOT override here -- if the user explicitly clears the Location field,
        // the form value will be undefined and should remain unset (server defaults to null).
        // Player color is a direct string, not an object.
        // The form doesn't have a color field for players, so it will be null/undefined.
      };
    },
    onOpen: (item) => {
      const stats = {
        str: item?.statStr ?? 10,
        dex: item?.statDex ?? 10,
        con: item?.statCon ?? 10,
        int: item?.statInt ?? 10,
        wis: item?.statWis ?? 10,
        cha: item?.statCha ?? 10,
      };
      return { formValues: { stats }, internalState: { attacks: item?.attacks ? [...item.attacks as unknown as AttackWithId[]] : [] } };
    },
    // onSave is no longer needed here, the generic handler in EditModal.tsx handles it.
  }),
  monsterRoster: ({
    title: 'Monster',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'maxHp', label: 'Max HP', type: 'number', rules: [{ required: true }] },
      { name: 'hitDice', label: 'Hit Dice' },
      { name: 'ac', label: 'AC', type: 'number', rules: [{ required: true }] },
      { name: 'dexMod', label: 'DEX Mod', type: 'number', rules: [{ required: true }] },
      { name: 'thac0', label: 'THAC0', type: 'number' },
      { name: 'numberOfAttacks', label: '# of Attacks', type: 'number' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      { name: 'attacks', label: 'Attacks', type: 'custom', component: AttackFields },
    ],
    onOpen: (item) => ({
      internalState: { attacks: item?.attacks ? [...item.attacks as unknown as AttackWithId[]] : [] }
    }),
    buttons: { setImage: true },
    addAction: actions.addMonster,
    updateAction: actions.updateMonster,
    createItemFromForm: (data) => {
      if (!data.name) return null;
      return { id: -Date.now(), ...data, hp: data.maxHp ?? 10 };
    },
  }),
  npc: ({
    title: 'NPC',
    fields: ([
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'status', label: 'Status', type: 'select', options: NPC_STATUS_OPTIONS },
      { name: 'poiId', label: 'Location', type: 'select', options: (_item: ItemWithId | null, state: StoreState) =>
          (state.session.roster.pois ?? []).map((poi: POI) => ({ label: poi.name, value: poi.id })),
        placeholder: '-- No Associated POI --'
      },
      { name: 'level', label: 'Level', type: 'number' },
      { name: 'thac0', label: 'THAC0', type: 'number' },
      { name: 'numberOfAttacks', label: '# of Attacks', type: 'number' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      { name: 'attacks', label: 'Attacks', type: 'custom', component: AttackFields },
    ] as FormField[]),
    addAction: actions.addNPC,
    updateAction: actions.updateNPC,
    buttons: { viewOnMap: true, setImage: true }, // viewOnMap is a custom button
    onOpen: (item) => ({
      internalState: { attacks: item?.attacks ? [...item.attacks as unknown as AttackWithId[]] : [] }
    }),
    createItemFromForm: (data) => {
      if (!data.name) return null;
      return { id: -Date.now(), ...data, showOnMap: false, x: 50, y: 50, attacks: [] };
    },
    // onSave is no longer needed here.
  }),
  poi: ({
    title: 'Point of Interest',
    fields: ([
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'status', label: 'Status', type: 'select', options: POI_STATUS_OPTIONS },
      { name: 'deityId', label: 'Deity', type: 'select', options: (_item: ItemWithId | null, state: StoreState) => {
          const deities = state.session.cache.deities;
          return (deities ?? []).map((d) => ({ label: d.name, value: d.id }));
        },
        placeholder: '-- No Associated Deity --'
      },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ] as FormField[]),
    addAction: actions.addPOI,
    updateAction: actions.updatePOI,
    buttons: { viewOnMap: true, setImage: true }, // viewOnMap is a custom button
    createItemFromForm: (data) => {
      if (!data.name) return null;
      return { id: -Date.now(), ...data, showOnMap: false, x: 50, y: 50 };
    },
  }),
  deity: ({
    title: 'Deity',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'alignment', label: 'Alignment', type: 'select', options: ALIGNMENT_OPTIONS },
      { name: 'divineRank', label: 'Divine Rank' },
      { name: 'domains', label: 'Domains' },
      { name: 'symbol', label: 'Symbol' },
      { name: 'info', label: 'Info', type: 'textarea', rows: 6 },
    ],
    buttons: { setImage: true },
    addAction: actions.addDeity,
    updateAction: actions.updateDeity,
    createItemFromForm: (data) => {
      if (!data.name) return null;
      return { id: -Date.now(), ...data };
    },
  }),
  lore: ({
    title: 'Lore Entry',
    fields: [
      { name: 'title', label: 'Title', rules: [{ required: true }] },
      { name: 'categoryId', label: 'Category', type: 'select', options: (_item: ItemWithId | null, state: StoreState) => {
        const loreCategories = state.session.cache.loreCategories;
        return (loreCategories ?? []).map((c) => ({ label: c.name, value: c.id }));
      }},
      { name: 'content', label: 'Content', type: 'textarea', rows: 10 },
    ],
    addAction: actions.addLore,
    updateAction: actions.updateLore,
    createItemFromForm: (data) => {
      if (!data.title) return null;
      return { id: -Date.now(), ...data };
    },
    buttons: {},
    // onSave is no longer needed here.
  }),
  mapRegion: ({
    title: 'Map Region',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'color', label: 'Color & Opacity', type: 'custom', component: ColorOpacityInputWrapper },
      { name: 'terrainTypeId', label: 'Terrain Type', type: 'select', options: (_item: ItemWithId | null, state: StoreState) => {
          const terrainTypes = state.session.cache.terrainTypes || [];
          return (terrainTypes ?? []).map((t) => ({ label: t.name, value: t.id }));
        },
        placeholder: '-- None --'
      },
      { name: 'mapId', label: 'Map', type: 'select', options: getMapOptions, rules: [{ required: true }], placeholder: '-- Select a Map --' },
    ],
    addAction: actions.addMapRegion,
    updateAction: actions.updateMapRegion,
    onOpen: (item) => {
      const formValues = {
        color: {
          color: item?.color ?? '#9c27b0',
          opacity: item?.opacity ? Number(item.opacity) * 100 : 80,
        },
      };
      return { formValues };
    },
    // onSave needs to transform the opacity value before it's sent to the action.
    onSave: (formValues) => {
      const { color } = formValues as { color?: { color: string; opacity: number } };
      return { color: color?.color, opacity: (color?.opacity ?? 80) / 100 };
    },
    createItemFromForm: (data, state) => {
      const { color, ...rest } = data;
      return {
        id: -Date.now(),
        ...rest,
        mapId: data.mapId ?? state.session.map.activeMapId ?? (() => { throw new Error('Cannot create a region without a mapId'); })(),
        type: 'freeform',
        visible: true,
        cells: [],
        color: color?.color, // This will be a string like '#RRGGBB'
        opacity: (color?.opacity ?? 80) / 100, // This will be a number like 0.8
      };
    },
  }),
  mapDrawing: ({
    title: 'Map Drawing',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'color', label: 'Color', type: 'color' },
      { name: 'mapId', label: 'Map', type: 'select', options: getMapOptions, rules: [{ required: true }], placeholder: '-- Select a Map --' },
    ],
    addAction: actions.addMapDrawing,
    updateAction: actions.updateMapDrawing,
    // onSave is no longer needed here.
  }),
  imageLibrary: ({
    title: 'Image',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'categoryId', label: 'Category', type: 'select', options: (_item: ItemWithId | null, state: StoreState) => {
        const imageCategories = state.session.cache.imageCategories;
        return (imageCategories ?? []).map((c: ImageCategory) => ({ label: c.name, value: c.id }));
      }},
    ],
    // The addAction for images is handled by a separate modal (AddImageModal).
    updateAction: useImageLibraryStore.getState().updateImage,
  }),
  encounter: ({
    title: 'Encounter Table',
    width: 800,
    fields: [
      { name: 'name', label: 'Table Name', rules: [{ required: true }] },
      { name: 'mapId', label: 'Map', type: 'select', options: getMapOptions, rules: [{ required: true }], placeholder: '-- Select a Map --' },
      { name: 'regionId', label: 'Region', type: 'select', options: (item: ItemWithId | null, state: StoreState) => {
          const regionsForMap = item?.mapId ? state.session.map.mapRegions.filter((r: MapRegion) => r.mapId === item.mapId) : [];
          return [
            { label: '* Entire Map', value: null }, // Use null for "Entire Map"
            ...(regionsForMap ?? []).map((r: MapRegion) => ({ label: r.name, value: r.id }))
          ];
        },
      },
      { name: 'creatures', label: 'Creatures', type: 'custom', component: EncounterCreatureFields },
    ],
    addAction: actions.addEncounter,
    updateAction: actions.updateEncounter,
    onValuesChange: (changedValues, _allValues, form) => {
      if (changedValues.mapId) (form as FormInstance).setFieldsValue({ regionId: undefined });
    },
    onOpen: (item) => ({
      internalState: { creatures: item?.creatures ? [...item.creatures] : [] }
    }),
    createItemFromForm: (data) => {
      if (!data.name || !data.mapId) return null;
      return { id: -Date.now(), name: data.name, mapId: data.mapId, regionId: data.regionId, creatures: [] };
    },
  }),
  plot: ({
    title: 'Plot',
    fields: [
      { name: 'plotName', label: 'Plot Details', type: 'textarea', rows: 4, rules: [{ required: true }] },
      { name: 'completed', label: 'Completed', type: 'checkbox' },
    ],
    addAction: (data: { plotName: string }) => actions.addPlot(data.plotName),
    updateAction: actions.updatePlot,
    createItemFromForm: (data) => {
      if (!data.plotName) return null;
      return { id: -Date.now(), ...data, completed: data.completed ?? false };
    },
    buttons: { setImage: true, setSymbolImage: true },
  }),
  loreCategory: ({
    title: 'Lore Category',
    fields: [
      { name: 'name', label: 'Category Name', rules: [{ required: true }] },
    ],
    addAction: actions.addLoreCategory,
    updateAction: actions.updateLoreCategory,
  }),
  imageCategory: ({
    title: 'Image Category',
    fields: [
      { name: 'name', label: 'Category Name', rules: [{ required: true }] },
      { name: 'isMapCategory', label: 'Use as Map Category', type: 'checkbox' },
    ],
    addAction: actions.addImageCategory,
    updateAction: actions.updateImageCategory,
  }),
  terrainType: ({
    title: 'Terrain Type',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'color', label: 'Color', type: 'color', initialValue: '#808080' },
    ],
    addAction: actions.addTerrainType,
    updateAction: actions.updateTerrainType,
    createItemFromForm: (data) => {
      return { id: -Date.now(), ...data };
    },
  }),
  soundscape: ({
    title: 'Soundscape',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'url', label: 'URL', rules: [{ required: true }, { type: 'url', message: 'Please enter a valid URL.' }] },
    ],
    addAction: actions.addSoundscape,
    updateAction: actions.updateSoundscape,
    createItemFromForm: (data) => {
      if (!data.name || !data.url) return null;
      return { id: -Date.now(), ...data };
    },
  }),
  soundscapeCategory: ({
    title: 'Soundscape Category',
    fields: [
      { name: 'name', label: 'Category Name', rules: [{ required: true }] },
    ],
    addAction: actions.addSoundscapeCategory,
    updateAction: actions.updateSoundscapeCategory,
  }),
  item: ({
    title: 'Item',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'cost', label: 'Cost (in copper)', type: 'number' },
      { name: 'weight', label: 'Weight (in lbs)', type: 'number' },
    ],
    addAction: actions.addItem,
    updateAction: actions.updateItem,
  }),
  spell: ({
    title: 'Spell',
    width: 600,
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
      { name: 'level', label: 'Level', type: 'number', rules: [{ required: true, type: 'number', min: 0, max: 9 }] },
      { name: 'school', label: 'School' },
      { name: 'castingTime', label: 'Casting Time' },
      { name: 'range', label: 'Range' },
      { name: 'components', label: 'Components' },
      { name: 'duration', label: 'Duration' },
      { name: 'description', label: 'Description', type: 'textarea', rows: 6 },
    ],
    addAction: actions.addSpell,
    updateAction: actions.updateSpell,
    createItemFromForm: (data) => ({ id: -Date.now(), ...data }),
    buttons: {},
  }),
  // Default/fallback config (remains loosely typed)
  default: {
    title: 'Item',
    fields: [
      { name: 'name', label: 'Name', rules: [{ required: true }] },
    ],
    createItemFromForm: (data: Partial<ItemWithId>) => {
      if (!data.name) return null;
      return { id: -Date.now(), ...data };
    },
    onSave: () => {},
  }
});